<template>
    <div class="annual-plan-initiatives">
        <div class="card mb-3">
            <div class="card-body">
                <h3>Projected Results</h3>
                <p>Results last calculated at {{ getMaxDate() }}.</p>
                <div class="date-selector">
                    <button type="button" class="btn btn-sm btn-outline-secondary back-btn" @click="moveDatesBackward"><i class="fa fa-angle-double-left" aria-hidden="true"></i></button>
                    {{ month_names[current_month] }} {{ current_year }} &mdash; {{ future_date }}
                    <button type="button" class="btn btn-sm btn-outline-secondary forward-btn" @click="moveDatesForward"><i class="fa fa-angle-double-right" aria-hidden="true"></i></button>
                </div>

                <div class="initiatives-selections">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Initiative</th>
                                <th class="priority">Priority</th>
                                <th>Strategic Focus</th>
                                <th class="metric">Cost</th>
                                <th class="metric">Emails Acquired</th>
                                <th class="metric">New Donors</th>
                                <th class="metric">Initiative Revenue</th>
                                <th class="metric">Recurring Revenue</th>
                                <th class="ltv">Lifetime Value Potential</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="item in results" :key="item.id">
                                <tr v-if="item.timeframe === current_year" 
                                    class="initiative" 
                                    :class="[item.priority.toLowerCase(), isEmptyClass(item)]"
                                    v-show="resultInPlan(item)">
                                    <td class="bare" scope="colgroup" v-if="item.priority === 'Required'"></td>
                                    <td :colspan="item.priority === 'Required' ? 2 : 1">{{ item.title }}</td>
                                    <td class="priority" v-if="item.priority !== 'Required'">{{ item.priority }}</td>
                                    <td v-if="item.priority !== 'Required'">
                                        <i class="fas" :class="getIcon(item.strategic_focus)"></i>
                                        {{ item.strategic_focus }}
                                    </td>
                                    <td class="text-left metric">{{ currency_na(getCostByInitiative(item)) }}</td>
                                    <td class="text-start metric">{{ number_na(item.emails) }}</td>
                                    <td class="text-start metric">{{ number_na(item.new_donors) }}</td>
                                    <td class="text-start metric">{{ currency_na(item.revenue) }}</td>
                                    <td class="text-start metric">{{ currency_na(item.recurring_revenue) }}</td>
                                    <td class="text-start ltv">{{ currency_na(item.lifetime_value) }}</td>
                                </tr>
                            </template>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th class="bare"></th>
                                <th class="border-white" colspan="2" style="text-align: center; vertical-align: middle;">Totals</th>
                                <th class="text-left metric border-white">{{ currency_na(getAllCosts()) }}</th>
                                <th class="text-start metric border-white">{{ number_na(getTotalByMetric('emails')) }}</th>
                                <th class="text-start metric border-white">{{ number_na(getTotalByMetric('new_donors')) }}</th>
                                <th class="text-start metric p-0 border-white" colspan="2">
                                    <table class="table mb-0">
                                        <tbody>
                                            <tr>
                                                <td class="text-start metric h-auto border-top-0 border-start-0 py-2 border-white" style="padding-left: 0.75rem; background-color:rgb(225, 229, 238);">
                                                    {{ currency_na(getTotalByMetric('revenue')) }}
                                                </td>
                                                <td class="text-start metric h-auto border-top-0 border-end-0 py-2 border-white" style="padding-left: 0.75rem;background-color:rgb(225, 229, 238);">
                                                    {{ currency_na(getTotalByMetric('recurring_revenue')) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colspan="2" class="metric h-auto border-bottom-0 border-start-0 border-end-0 py-2" style="background-color:rgb(225, 229, 238);">
                                                    <span class="fw-normal">Total:</span> {{ currency_na(getTotalByMetric('revenue') + getTotalByMetric('recurring_revenue')) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </th>
                                <th class="text-start ltv border-white">{{ currency_na(getTotalByMetric('lifetime_value')) }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <h3>Result Details</h3>
                <div class="btn-group result-selector" role="group">
                    <button v-for="metric in ['emails', 'new_donors', 'gifts', 'revenue', 'recurring_revenue', 'lifetime_value']"
                            :key="metric"
                            type="button" 
                            class="btn" 
                            @click="current_metric = metric"
                            :class="current_metric === metric ? 'btn-secondary' : 'btn-outline-secondary'">
                        {{ metric === 'lifetime_value' ? 'LTV' : metric.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }}
                    </button>
                </div>

                <div class="initiatives-selections">
                    <table class="table">
                        <thead>
                            <tr>
                                <th colspan="2" scope="colgroup" class="bare"></th>
                                <template v-for="(months, year) in calendar_headers" :key="year">
                                    <th :colspan="months.length" scope="colgroup">{{ year }}</th>
                                </template>
                            </tr>
                            <tr>
                                <th>Name</th>
                                <th class="priority">Priority</th>
                                <template v-for="(months, year) in calendar_headers" :key="year">
                                    <th v-for="month in months" 
                                        :key="month" 
                                        scope="col" 
                                        class="month">
                                        {{ month_names[month] }}
                                    </th>
                                </template>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="row in results" :key="row.id">
                                <tr v-if="row.timeframe === current_year && resultInPlan(row)"
                                    :class="row.priority.toLowerCase()">
                                    <td scope="row">{{ row.title }}</td>
                                    <td class="priority">{{ row.priority }}</td>
                                    <template v-for="(months, year) in calendar_headers" :key="year">
                                        <td v-for="month in months" 
                                            :key="month" 
                                            scope="col" 
                                            class="text-center month">
                                            <span v-if="['emails', 'new_donors', 'gifts'].includes(current_metric)">
                                                {{ number(getMetric(row, month, year)) }}
                                            </span>
                                            <span v-else>
                                                {{ currency(getMetric(row, month, year)) }}
                                            </span>
                                        </td>
                                    </template>
                                </tr>
                            </template>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="2" class="border-white" style="text-align: center; vertical-align: middle;">Total</th>
                                <template v-for="(months, year) in calendar_headers" :key="year">
                                    <th v-for="month in months" 
                                        :key="month" 
                                        scope="col" 
                                        class="text-center month border-white">
                                        <span v-if="['emails', 'new_donors', 'gifts'].includes(current_metric)">
                                            {{ number(getTotalByMonth(month, year)) }}
                                        </span>
                                        <span v-else>
                                            {{ currency(getTotalByMonth(month, year)) }}
                                        </span>
                                    </th>
                                </template>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'AnnualPlanResults',
    props: {
        plan: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        results: {
            type: Array,
            required: true
        },
        result_details: {
            type: Array,
            required: true
        },
        current_month: {
            type: Number,
            required: true
        },
        current_year: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            month_names: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
            current_metric: 'emails',
            result_arr: [],
            num_calls: 0
        };
    },
    beforeMount() {
        //Need to make the results into an easily accessible array to speed up the search time
        for(var i = 0; i < this.result_details.length; i++) {
            var year = parseInt(this.result_details[i].date.split("-")[0]);
            var month = parseInt(this.result_details[i].date.split("-")[1])-1;

            this.result_arr[year+"-"+month+"-"+this.result_details[i].initiative_id] = this.result_details[i];
        }
    },
    watch: {
        result_details: {
            handler(newVal) {
                this.result_arr = [];
                //Need to make the results into an easily accessible array to speed up the search time
                for(var i = 0; i < newVal.length; i++) {
                    var year = parseInt(newVal[i].date.split("-")[0]);
                    var month = parseInt(newVal[i].date.split("-")[1])-1;

                    this.result_arr[year+"-"+month+'-'+newVal[i].initiative_id] = newVal[i];
                }
            },
            deep: true
        }
    },
    computed: {
        future_date() {
            return moment().set({ 'year': this.current_year, 'month': this.current_month }).add(11, 'M').format('MMM YYYY');
        },
        calendar_headers() {
            var headers = {};

            if (this.current_month == null || this.current_year == null)
                return headers;

            let startDate = moment().set({ 'year': this.current_year, 'month': this.current_month });
            let endDate = moment().set({ 'year': this.current_year, 'month': this.current_month }).add(11, 'M')
            while (endDate.isSameOrAfter(startDate)) {
                if (!headers[startDate.year()])
                    headers[startDate.year()] = [];

                headers[startDate.year()].push(startDate.month());
                //increment by one day
                startDate = startDate.add(1, 'M')
            }

            return headers;
        }
    },
    methods: {
        getMaxDate() {
            if(this.results == null || this.results.length == 0)
                return null;

            var dte = new Date(this.results[0].updated_at + " UTC");
            return dte.toLocaleString();
        },
        isEmptyClass(item) {
            if(this.getCostByInitiative(item) > 0 ||
                parseInt(item.emails) + parseInt(item.new_donors) + parseFloat(item.revenue) + parseFloat(item.recurring_revenue) + parseFloat(item.lifetime_value) > 0)
                return "";
            return "is-empty";
        },
        getIcon(focus) {
            if(focus == 'Analysis & Reporting')
                return 'fa-chart-bar';
            if(focus == 'Acquisition')
                return 'fa-filter';
            if(focus == 'Audience Generation')
                return 'fa-users';
            if(focus == 'Donor Upgrade')
                return 'fa-arrow-up';
            if(focus == 'Reactivation')
                return 'fa-redo';
            if(focus == 'Retention')
                return 'fa-coins';
        },
        resultInPlan(init) {
            var init_arr = init.initiative_ids.split(",");
            for(var j = 0; j < this.plan.initiatives.length; j++)
                if(init_arr.includes(this.plan.initiatives[j].id + ""))
                    return true;

            return false;
        },
        moveDatesBackward() {
            var timeframe = moment().set({ 'year': this.current_year, 'month': this.current_month }).subtract(12, 'M');
            this.$emit('update:current_month', timeframe.month());
            this.$emit('update:current_year', timeframe.year());
        },
        moveDatesForward() {
            var timeframe = moment().set({ 'year': this.current_year, 'month': this.current_month }).add(12, 'M');
            this.$emit('update:current_month', timeframe.month());
            this.$emit('update:current_year', timeframe.year());
        },
        getCostByInitiative(initiative_needed) {
            var start_date = new Date(Date.UTC(this.current_year,this.current_month));
            var timeframe = moment().set({ 'year': this.current_year, 'month': this.current_month }).add(11, 'M');
            var end_date = new Date(Date.UTC(timeframe.year(),timeframe.month()));

            var total = 0;
            //Look at each initiative
            for(var i = 0; i < this.plan.initiatives.length; i++){
                //If this init matches the title and priority of the one in the loop
                if(this.plan.initiatives[i].title == initiative_needed.title &&
                    this.plan.initiatives[i].priority == initiative_needed.priority) {

                    var init = this.plan.initiatives[i];
                    var init_start_date = this.getJSDate(init.start_month);
                    if(start_date > init_start_date)
                        init_start_date = start_date;

                    var init_end_date = this.getJSDate(init.end_month);
                    if(end_date < init_end_date)
                        init_end_date = end_date;

                    if(init_start_date > init_end_date) continue;

                    for(var j = 0; j < this.plan.initiatives[i].costs.length; j++) {
                        var cost = this.plan.initiatives[i].costs[j];

                        if(cost.type == "monthly")
                            //Have to add 1 to account the initial month (July - July would be 0 but should be 1)
                            total += parseFloat(cost.value) * (1+this.monthDiff(init_start_date, init_end_date));
                        else if(cost.type == 'one_time' && this.getJSDate(init.start_month) <= init_end_date && this.getJSDate(init.start_month) >= init_start_date)
                            total += parseFloat(cost.value);
                        else if(cost.type == 'media')
                            total += parseFloat(cost.value) * (1+ this.monthDiff(init_start_date, init_end_date));
                    }
                }
            }
            return total;
        },
        getAllCosts() {
            var total = 0;
            for(var i = 0; i < this.results.length; i++){
                if(this.results[i].timeframe == this.current_year && this.resultInPlan(this.results[i]))
                    total += this.getCostByInitiative(this.results[i]);
            }
            return total;
        },
        getTotalByMetric(metric) {
            var total = 0;
            for(var i = 0; i < this.results.length; i++){
                if(this.results[i].timeframe == this.current_year && this.resultInPlan(this.results[i])) {
                    if(metric == 'emails')
                        total += parseInt(this.results[i].emails);
                    else if(metric == 'new_donors')
                        total += parseInt(this.results[i].new_donors);
                    else if(metric == 'revenue')
                        total += parseFloat(this.results[i].revenue);
                    else if(metric == 'recurring_revenue')
                        total += parseFloat(this.results[i].recurring_revenue);
                    else if(metric == 'lifetime_value')
                        total += parseFloat(this.results[i].lifetime_value);
                    else if(metric == 'gifts')
                        total += parseFloat(this.results[i].gifts);
                }
            }
            return total;
        },
        getTotalByMonth(month, year) {
            var total = 0;
            var arr = [];
            for(let row in this.results)
                arr = arr.concat(this.results[row].initiative_ids.split(","));

            let unique_arr = Array.from(new Set(arr));

            for(var i = 0; i < unique_arr.length; i++) {
                var init_id = unique_arr[i];

                if(this.result_arr[year+'-'+month+'-'+init_id] == undefined)
                    return 0;

                if(this.current_metric == 'emails')
                    total += parseInt(this.result_arr[year+'-'+month+'-'+init_id].emails);
                else if(this.current_metric == 'new_donors')
                    total += parseInt(this.result_arr[year+'-'+month+'-'+init_id].new_donors);
                else if(this.current_metric == 'revenue')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].revenue);
                else if(this.current_metric == 'recurring_revenue')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].recurring_revenue);
                else if(this.current_metric == 'lifetime_value')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].lifetime_value);
                else if(this.current_metric == 'gifts')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].gifts);
            }
            return total;
        },
        getMetric(row, month, year) {
            var arr = row.initiative_ids.split(",");

            var total = 0;
            for(var i = 0; i < arr.length; i++) {
                var init_id = arr[i];

                if(this.result_arr[year+'-'+month+'-'+init_id] == undefined)
                    return 0;

                if(this.current_metric == 'emails')
                    total += parseInt(this.result_arr[year+'-'+month+'-'+init_id].emails);
                else if(this.current_metric == 'new_donors')
                    total += parseInt(this.result_arr[year+'-'+month+'-'+init_id].new_donors);
                else if(this.current_metric == 'revenue')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].revenue);
                else if(this.current_metric == 'recurring_revenue')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].recurring_revenue);
                else if(this.current_metric == 'lifetime_value')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].lifetime_value);
                else if(this.current_metric == 'gifts')
                    total += parseFloat(this.result_arr[year+'-'+month+'-'+init_id].gifts);
            }
            return total;
        },
        getJSDate(str_date) {
            var part = str_date.split('T')[0];
            var date = new Date(Date.UTC(part.split("-")[0], parseInt(part.split("-")[1])-1, part.split("-")[2]));
            return date;
        },
        monthDiff(d1, d2) {
            var months;
            months = (d2.getFullYear() - d1.getFullYear()) * 12;
            months -= d1.getMonth();
            months += d2.getMonth();
            return months <= 0 ? 0 : months;
        }
    }
});
</script>

